import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaInstagram, FaFacebookF, FaYoutube, FaEnvelope, FaPhoneAlt, FaHome, FaInfoCircle, FaCouch, FaUsers, FaBuilding, FaWhatsapp, FaSignOutAlt, FaUserCircle } from "react-icons/fa";
import Logo from "../image/Logo_Nav.webp";
import { useAuth } from "../AuthContext";
import "../App.css";

export default function Header() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const { isLoggedIn, isApproved, userProfile, logout, pendingUser, clearPendingStatus } = useAuth();

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1200) {
                setMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);

        if (menuOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.documentElement.style.overflowY = 'scroll';
        } else {
            const scrollY = document.body.style.top;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.documentElement.style.overflowY = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            document.documentElement.style.overflowY = '';
        };
    }, [menuOpen]);

    return (
        <>
            {/* Pending Registration Alert Banner */}
            {pendingUser && pendingUser.status === "pending" && !isLoggedIn && (
                <div className="pending-status-top-banner py-2 px-3 text-dark border-bottom">
                    <div className="container d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2" style={{ fontSize: '13px' }}>
                            <span style={{ fontSize: '16px' }}>⏳</span>
                            <span>
                                <strong>Registration Pending:</strong> Your request for <strong>{pendingUser.email}</strong> is pending admin approval. Products are visible, but prices will be displayed once approved.
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-dark btn-sm py-1 px-3 rounded-pill fw-semibold"
                                style={{ fontSize: '11px' }}
                                onClick={() => navigate('/login')}
                            >
                                Check Status
                            </button>
                            <button
                                type="button"
                                className="btn-close ms-1"
                                style={{ fontSize: '10px' }}
                                onClick={clearPendingStatus}
                                title="Dismiss"
                            ></button>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Blue Bar - Scrolls away on scroll */}
            <div className="header-top-bar d-none d-xl-block py-1">
                <div className="container d-flex flex-wrap align-items-center justify-content-between">
                    <div className="top-bar-left">
                        Welcome To Shree Kamdhenu Furniture
                    </div>
                    <div className="top-bar-center d-flex align-items-center gap-4">
                        <span className="d-flex align-items-center gap-1"><FaEnvelope /> <a href="mailto:kamdhenusteelindustries99@gmail.com" className="text-light text-decoration-none">kamdhenusteelindustries99@gmail.com</a></span>
                        <span className="d-flex align-items-center gap-1"><FaPhoneAlt /> <a href="tel:+917202057575" className="text-light text-decoration-none">+91 72020 57575 <small className="opacity-75">(Customer Care)</small></a></span>
                    </div>
                    <div className="top-bar-right d-flex align-items-center gap-2">
                        <a href="https://www.facebook.com/kamdhenu.steelindustries.2025" target="_blank" rel="noopener noreferrer" className="top-social-icon" title="Facebook"><FaFacebookF /></a>
                        <a href="https://www.instagram.com/kamdhenusteelindustries1995/" target="_blank" rel="noopener noreferrer" className="top-social-icon" title="Instagram"><FaInstagram /></a>
                        <a href="https://www.youtube.com/@KAMDHENUSTEELINDUSTRIES" target="_blank" rel="noopener noreferrer" className="top-social-icon" title="YouTube"><FaYoutube /></a>
                        <a href="https://wa.me/919924382499?text=Hello!%20I%20would%20like%20to%20inquire%20about%20Shree%20Kamdhenu%20Furniture." target="_blank" rel="noopener noreferrer" className="top-social-icon" title="Chat on WhatsApp"><FaWhatsapp /></a>
                    </div>
                </div>
            </div>

            {/* Main Navigation Bar - Sticky at top when scrolling */}
            <header className="header-main-nav sticky-top top-0 z-3 bg-white shadow-sm">
                <div className="container d-flex align-items-center justify-content-between">
                    {/* Left: Brand */}
                    <a
                        href="/"
                        onClick={(e) => {
                            if (e.ctrlKey || e.metaKey) return;
                            e.preventDefault();
                            navigate("/");
                        }}
                        className="d-flex align-items-center gap-3 cursor-pointer brand-link py-2"
                    >
                        <img
                            src={Logo}
                            alt="Shree Kamdhenu Furniture"
                            className="header-logo"
                        />
                    </a>

                    {/* Center: Desktop Navigation */}
                    <nav className="nav-center desktop-nav d-none d-xl-flex align-items-center">
                        <Link to="/" className={`nav-link ${pathname === '/' ? 'active' : ''}`}>Home</Link>
                        <Link to="/collections" className={`nav-link ${pathname === '/collections' ? 'active' : ''}`}>Our Products</Link>
                        <Link to="/about" className={`nav-link ${pathname === '/about' ? 'active' : ''}`}>About Us</Link>
                        <Link to="/clients" className={`nav-link ${pathname === '/clients' ? 'active' : ''}`}>Our Clients</Link>
                        <Link to="/infrastructure" className={`nav-link ${pathname === '/infrastructure' ? 'active' : ''}`}>Project Gallery</Link>
                        <Link to="/contact" className={`nav-link ${pathname === '/contact' ? 'active' : ''}`}>Contact Us</Link>
                    </nav>

                    {/* Right: Login/User + Inquiry Button */}
                    <div className="nav-right d-none d-xl-flex align-items-center gap-2">
                        {isLoggedIn && isApproved ? (
                            <div className="header-user-menu">
                                <button className="header-user-btn" onClick={() => document.getElementById('header-user-dropdown')?.classList.toggle('show')}>
                                    <FaUserCircle size={18} />
                                    <span>{userProfile?.name?.split(' ')[0] || 'Account'}</span>
                                </button>
                                <div className="header-user-dropdown" id="header-user-dropdown">
                                    <div className="header-user-info">
                                        <strong>{userProfile?.name}</strong>
                                        <small>{userProfile?.company}</small>
                                    </div>
                                    <hr style={{ margin: '6px 0' }} />
                                    <button className="header-logout-btn" onClick={async () => { await logout(); navigate('/'); }}>
                                        <FaSignOutAlt size={14} /> Logout
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <Link to="/login" className="btn btn-outline-dark btn-sm rounded-pill px-3 py-1 fw-semibold" style={{ fontSize: '13px' }}>
                                <FaUserCircle className="me-1" /> Dealer Login
                            </Link>
                        )}
                        <a href="https://wa.me/919924382499?text=Hello!%20I%20would%20like%20to%20inquire%20about%20Shree%20Kamdhenu%20Furniture." target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-inquiry">Inquiry Now &rarr;</a>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className={`mobile-menu-btn creative-hamburger d-xl-none ${menuOpen ? 'open' : ''}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>

                {/* Mobile Nav Overlay (Sidebar) */}
                <div className={`mobile-nav-backdrop d-xl-none ${menuOpen ? 'show' : ''}`} onClick={() => setMenuOpen(false)}></div>
                <div className={`mobile-nav-overlay d-xl-none gap-0 ${menuOpen ? 'open' : ''}`}>
                    <div className="mobile-sidebar-header p-3 border-bottom d-flex align-items-center justify-content-between bg-white">
                        <img src={Logo} alt="Shree Kamdhenu Furniture" style={{ height: '55px', objectFit: 'contain' }} />
                        <button className="btn-close" onClick={() => setMenuOpen(false)} aria-label="Close"></button>
                    </div>

                    <div className="px-3 py-2 bg-light text-center border-bottom text-muted d-flex align-items-center justify-content-center gap-3" style={{ fontSize: '12px', fontWeight: '600' }}>
                        <span>ISO 9001:2015</span>
                        <span className="text-muted">•</span>
                        <span>GeM Registered</span>
                    </div>

                    <div className="d-flex flex-column gap-1 p-3">
                        <Link to="/" className={`mobile-nav-link ${pathname === '/' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                            <span className="mobile-icon-box"><FaHome /></span> Home
                        </Link>
                        <Link to="/collections" className={`mobile-nav-link ${pathname === '/collections' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                            <span className="mobile-icon-box"><FaCouch /></span> Our Products
                        </Link>
                        <Link to="/about" className={`mobile-nav-link ${pathname === '/about' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                            <span className="mobile-icon-box"><FaInfoCircle /></span> About Us
                        </Link>
                        <Link to="/clients" className={`mobile-nav-link ${pathname === '/clients' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                            <span className="mobile-icon-box"><FaUsers /></span> Our Clients
                        </Link>
                        <Link to="/infrastructure" className={`mobile-nav-link ${pathname === '/infrastructure' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                            <span className="mobile-icon-box"><FaBuilding /></span> Project Gallery
                        </Link>
                        <Link to="/contact" className={`mobile-nav-link ${pathname === '/contact' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                            <span className="mobile-icon-box"><FaEnvelope /></span> Contact Us
                        </Link>

                        <hr className="my-2" />
                        {isLoggedIn && isApproved ? (
                            <button className="mobile-nav-link text-start border-0 bg-transparent" onClick={async () => { await logout(); setMenuOpen(false); navigate('/'); }}>
                                <span className="mobile-icon-box"><FaSignOutAlt /></span> Logout ({userProfile?.name?.split(' ')[0]})
                            </button>
                        ) : (
                            <Link to="/login" className={`mobile-nav-link ${pathname === '/login' ? 'active' : ''}`} onClick={() => setMenuOpen(false)}>
                                <span className="mobile-icon-box"><FaUserCircle /></span> Dealer Login
                            </Link>
                        )}
                    </div>

                    <div className="mobile-sidebar-footer p-3 bg-light mt-auto border-top">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '13px' }}>Connect With Us</h6>
                            <div className="d-flex gap-2">
                                <a href="https://www.facebook.com/kamdhenu.steelindustries.2025" target="_blank" rel="noopener noreferrer" className="top-social-icon text-dark bg-white shadow-sm" title="Facebook"><FaFacebookF /></a>
                                <a href="https://www.instagram.com/kamdhenusteelindustries1995/" target="_blank" rel="noopener noreferrer" className="top-social-icon text-dark bg-white shadow-sm" title="Instagram"><FaInstagram /></a>
                                <a href="https://www.youtube.com/@KAMDHENUSTEELINDUSTRIES" target="_blank" rel="noopener noreferrer" className="top-social-icon text-dark bg-white shadow-sm" title="YouTube"><FaYoutube /></a>
                                <a href="https://wa.me/919924382499?text=Hello!%20I%20would%20like%20to%20inquire%20about%20Shree%20Kamdhenu%20Furniture." target="_blank" rel="noopener noreferrer" className="top-social-icon text-dark bg-white shadow-sm" title="Chat on WhatsApp"><FaWhatsapp /></a>
                            </div>
                        </div>
                        <div className="d-flex flex-column gap-2 text-muted small mb-3">
                            <span className="d-flex align-items-center gap-2" style={{ fontSize: '12px' }}><FaEnvelope className="text-primary" /> <a href="mailto:kamdhenusteelindustries99@gmail.com" className="text-muted text-decoration-none">kamdhenusteelindustries99@gmail.com</a></span>
                            <span className="d-flex align-items-center gap-2" style={{ fontSize: '12px' }}><FaPhoneAlt className="text-primary" /> <a href="tel:+917202057575" className="text-muted text-decoration-none">+91 72020 57575</a></span>
                        </div>
                        <a href="https://wa.me/919924382499?text=Hello!%20I%20would%20like%20to%20inquire%20about%20Shree%20Kamdhenu%20Furniture." target="_blank" rel="noopener noreferrer" className="btn btn-primary w-100 rounded-pill py-2 shadow-sm d-flex justify-content-center align-items-center gap-2" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
                            Inquiry Now &rarr;
                        </a>
                    </div>
                </div>
            </header>
        </>
    );
}
